import { useCallback } from "react";
import { Card, Text } from "@ferrivia/ui";
import { DeliveryState, MessageBlocks, ReplyQuote, useSwipeToReply } from "@ferrivia/chat-ui";
import { useFormatters, useTranslation } from "@ferrivia/i18n";
import { ListChecks, Reply } from "lucide-react";
import type { ChatMessage, PatientAction } from "@ferrivia/api-contract";
import { MessageMenu } from "../MessageMenu";
import { useEntryContent } from "./entryContent";
import type { FeedEntry } from "./types";
import s from "./TimelineFeed.module.css";

export interface TimelineFeedProps {
  entries: FeedEntry[];
  /** The patient this thread belongs to — used to label the client side of a message bubble, since ChatMessage carries no name for the "client" author type. */
  patientName: string;
  onReply?: (message: Pick<ChatMessage, "id" | "authorType" | "authorName" | "body" | "redactedAt">) => void;
  /** Opens the Labs tab from an entry whose numbers live there. Absent when
      the feed is rendered without a context pane beside it. */
  onOpenLabs?: (entryKind: FeedEntry["kind"]) => void;
  /** Starts an action from a delivered client or staff message. Absent, the message menu offers no "Create action…". */
  onCreateAction?: (entry: Extract<FeedEntry, { kind: "message" }>) => void;
  /** Actions that came from a message, keyed by that message's id, for the source chip. */
  actionsByMessageId?: Map<string, PatientAction[]>;
  onOpenAction?: (actionId: string) => void;
}

function messageText(body: Extract<FeedEntry, { kind: "message" }>["body"]): string {
  return body.blocks
    .map((block) => (block.type === "text" && "text" in block ? String(block.text) : ""))
    .filter((text) => text !== "")
    .join("\n\n");
}

function isHumanMessage(
  entry: FeedEntry,
): entry is Extract<FeedEntry, { kind: "message" }> & { authorType: "client" | "staff" } {
  return entry.kind === "message" && (entry.authorType === "client" || entry.authorType === "staff");
}

function MessageBubbleRow({
  entry,
  isStaff,
  sender,
  timeLabel,
  onReply,
  onCreateAction,
  actions,
  onOpenAction,
}: {
  entry: Extract<FeedEntry, { kind: "message" }>;
  isStaff: boolean;
  sender: string;
  timeLabel: string;
  onReply?: () => void;
  onCreateAction?: () => void;
  actions?: PatientAction[];
  onOpenAction?: (actionId: string) => void;
}) {
  const { t } = useTranslation("clinic");
  const handleReply = useCallback(() => onReply?.(), [onReply]);
  const swipe = useSwipeToReply(handleReply);

  function scrollToMessage(id: string) {
    const el = document.querySelector(`[data-message-id="${id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add(s.highlightAnim);
      setTimeout(() => el.classList.remove(s.highlightAnim), 1500);
    }
  }

  return (
    <div className={`${s.msgRow} ${isStaff ? s.msgRowMe : s.msgRowThem}`}>
      <div className={s.bubbleWrap} data-message-id={entry.id}>
        <div
          className={`${s.msg} ${isStaff ? s.me : s.them}`}
          ref={swipe.ref}
          onPointerDown={swipe.onPointerDown}
          onPointerMove={swipe.onPointerMove}
          onPointerUp={swipe.onPointerUp}
          onPointerCancel={swipe.onPointerCancel}
        >
          {entry.replyTo ? (
            <ReplyQuote
              reply={entry.replyTo}
              isOwnReply={isStaff}
              onClick={() => scrollToMessage(entry.replyTo!.id)}
            />
          ) : null}
          <div className={s.sender}>{sender}</div>
          <MessageBlocks body={entry.body} />
          <div className={s.msgFoot}>
            <span className={s.time}>{timeLabel}</span>
            {entry.delivery ? <DeliveryState state={entry.delivery.state} onRetry={entry.delivery.onRetry} /> : null}
          </div>
          {actions && actions.length > 0 && onOpenAction ? (
            <button type="button" className={s.sourceChip} onClick={() => onOpenAction(actions[actions.length - 1].id)}>
              <ListChecks size={14} aria-hidden="true" />
              {actions.length === 1
                ? t("timelineFeed.actionChip", { title: actions[0].title, defaultValue: "Action: {{title}}" })
                : t("timelineFeed.actionChipMany", { count: actions.length, defaultValue: "{{count}} actions" })}
            </button>
          ) : null}
        </div>
        <div ref={swipe.iconRef} className={s.swipeIndicator} aria-hidden="true">
          <Reply size={18} />
        </div>
      </div>
      {onReply ? (
        <button
          type="button"
          className={s.hoverReply}
          onClick={handleReply}
          aria-label={t("timelineFeed.reply", "Reply")}
        >
          <Reply size={16} aria-hidden="true" />
        </button>
      ) : null}
      {!entry.delivery ? (
        <MessageMenu
          triggerClassName={s.hoverReply}
          onReply={onReply}
          onCopy={() => void navigator.clipboard?.writeText(messageText(entry.body))}
          onCreateAction={onCreateAction}
        />
      ) : null}
    </div>
  );
}

export function TimelineFeed({
  entries,
  patientName,
  onReply,
  onOpenLabs,
  onCreateAction,
  actionsByMessageId,
  onOpenAction,
}: TimelineFeedProps) {
  const { t } = useTranslation("clinic");
  const { tag } = useFormatters();
  const entryContent = useEntryContent();

  function genericMessageTitle(authorType: Extract<FeedEntry, { kind: "message" }>["authorType"]): string {
    switch (authorType) {
      case "assistant":
        return t("timelineFeed.assistantMessage", "AI assistant message");
      case "system":
        return t("timelineFeed.systemMessage", "System message");
      default:
        return "";
    }
  }

  function dayLabel(occurredAt: string): string {
    return new Intl.DateTimeFormat(tag, { weekday: "long", day: "numeric", month: "long" }).format(
      new Date(occurredAt),
    );
  }

  function timeLabelFn(occurredAt: string): string {
    return new Intl.DateTimeFormat(tag, { hour: "2-digit", minute: "2-digit", hour12: false }).format(
      new Date(occurredAt),
    );
  }

  const oldestFirst = [...entries].reverse();

  let lastDay: string | null = null;

  return (
    <ol className={s.root}>
      {oldestFirst.map((entry) => {
        const day = dayLabel(entry.occurredAt);
        const showDaySeparator = day !== lastDay;
        lastDay = day;

        if (isHumanMessage(entry)) {
          const isStaff = entry.authorType === "staff";
          const sender = isStaff ? (entry.authorName ?? t("timelineFeed.fallbackStaffName", "Staff")) : patientName;

          return (
            <li key={entry.id} className={s.item}>
              {showDaySeparator && (
                <div className={s.daySep}>
                  <span>{day}</span>
                </div>
              )}
              <MessageBubbleRow
                entry={entry}
                isStaff={isStaff}
                sender={sender}
                timeLabel={timeLabelFn(entry.occurredAt)}
                onReply={
                  onReply
                    ? () =>
                        onReply({
                          id: entry.id,
                          authorType: entry.authorType,
                          authorName: entry.authorName,
                          body: entry.body,
                          redactedAt: null,
                        })
                    : undefined
                }
                onCreateAction={onCreateAction ? () => onCreateAction(entry) : undefined}
                actions={actionsByMessageId?.get(entry.id)}
                onOpenAction={onOpenAction}
              />
            </li>
          );
        }

        // An assistant or system message renders as a card too, but it has no
        // domain facts to lay out — it is a message body under a generic
        // title, so it takes its own branch rather than being forced through
        // the per-kind content below.
        if (entry.kind === "message") {
          return (
            <li key={entry.id} className={s.item}>
              {showDaySeparator && (
                <div className={s.daySep}>
                  <span>{day}</span>
                </div>
              )}
              <Card className={s.card}>
                <div className={s.body}>
                  <Text as="p" size="sm" weight="bold" className={s.title}>
                    {genericMessageTitle(entry.authorType)}
                  </Text>
                  <Text as="p" size="xs" className={s.detail}>
                    <MessageBlocks body={entry.body} />
                  </Text>
                  <div className={s.foot}>
                    <span className={s.time}>{timeLabelFn(entry.occurredAt)}</span>
                  </div>
                </div>
              </Card>
            </li>
          );
        }

        const content = entryContent(entry);

        return (
          <li
            key={entry.id}
            className={s.item}
            data-action-id={entry.kind === "action" ? entry.action.id : undefined}
          >
            {showDaySeparator && (
              <div className={s.daySep}>
                <span>{day}</span>
              </div>
            )}
            <Card className={s.card}>
              <span className={content.tint === "lab" ? `${s.icon} ${s.iconLab}` : s.icon} aria-hidden="true">
                <content.Icon size={18} />
              </span>
              <div className={s.body}>
                <Text as="p" size="sm" weight="bold" className={s.title}>
                  {content.title}
                </Text>
                {content.lines
                  .filter((line) => line.length > 0)
                  .map((line, index) => (
                    // Positional: two detail lines can legitimately read the
                    // same, so the line itself is not a key.
                    <Text key={`${entry.id}:${index}`} as="p" size="xs" className={s.detail}>
                      {line}
                    </Text>
                  ))}
                {content.supersede && (
                  <Text as="p" size="xs" className={s.supersede}>
                    {content.supersede}
                  </Text>
                )}
                <div className={s.foot}>
                  <span className={s.time}>{timeLabelFn(entry.occurredAt)}</span>
                  {content.opensLabs && onOpenLabs && (
                    <button type="button" className={s.cardLink} onClick={() => onOpenLabs(entry.kind)}>
                      {t("timelineFeed.openInLabs", "Open in Labs")}
                    </button>
                  )}
                  {content.opensAction && onOpenAction && (
                    <button type="button" className={s.cardLink} onClick={() => onOpenAction(content.opensAction!)}>
                      {t("timelineFeed.openInPlan", "Open in plan")}
                    </button>
                  )}
                </div>
              </div>
            </Card>
          </li>
        );
      })}
    </ol>
  );
}
